<template>
  <div class="funnel-table-container">
    <mf-card-container v-model="search" title="Cupons por loja" description="Desdobramento do funil de cupons em cada loja" useSearch placeholder="Buscar loja">
      <template v-slot:filters>
        <export-data :data-fetcher="getDataToExport" filename="resultados_cupons_por_loja" ghost />
        <headers-data-table-settings
          key="offer_results_coupons_per_unit"
          :headers.sync="headers"
          :default-headers="getDefaultHeadersOptions()"
          headers-settings-name="offer_results_coupons_per_unit"
        />
      </template>
      <v-data-table
        class="units-table"
        :headers="computedHeaders"
        :items="items"
        :search="search"
        :footer-props="footerProps"
        sort-desc
        sort-by="coupons_with_products"
      >
        <template v-for="h in headers" v-slot:[`header.${h.value}`]="{ header }">
          <mf-tooltip v-bind:key="h.text" top max-width="192px" :disabled="!headersTooltips[h.value]">
            <template v-slot:activator="{ on }">
              {{ h.text }}
              <mf-icon v-if="headersTooltips[h.value]" small color="grey" v-on="on">info</mf-icon>
            </template>
            <span>{{ headersTooltips[h.value] }}</span>
          </mf-tooltip>
        </template>

        <template v-slot:[`item.unit_name`]="{ item }">
          <span class="units-table-unit-name">{{ item.unit_name }}</span>
        </template>

        <template v-slot:[`item.coupons_club_with_products_percentage`]="{ item }">
          <percent-color-chip :value="item.coupons_club_with_products_percentage" :highest-value="couponsClubHighestValue" :offer-type="offerType" />
        </template>

        <template v-slot:[`item.coupons_eligible_percentage`]="{ item }">
          <percent-color-chip :value="item.coupons_eligible_percentage" :highest-value="couponsEligibleHighestValue" :offer-type="offerType" />
        </template>

        <template v-slot:[`item.coupons_activated_percentage`]="{ item }">
          <percent-color-chip :value="item.coupons_activated_percentage" :highest-value="couponsActivatedHighestValue" :offer-type="offerType" />
        </template>
      </v-data-table>
    </mf-card-container>
  </div>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep'
import ExportData from '@/components/export-data/ExportData.vue'
import HeadersDataTableSettings from '@/components/HeadersDataTableSettings.vue'
import PercentColorChip from '@/modules/industria/ofertas-trade/views/components/PercentColorChip.vue'

export default {
  name: 'FunnelTable',
  components: {
    PercentColorChip,
    ExportData,
    HeadersDataTableSettings,
  },
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    offerType: {
      type: String,
      validator: (value) =>
        [
          'general',
          'percentage',
          'dynamic_pricing_percentage',
          'takepay',
          'combo',
          'cashback_percentage',
          'cashback_absolute',
          'points',
          'nextbuycashback',
          'dynamic_rule',
          'crosssell',
        ].includes(value),
    },
    metrics: {
      type: [Array, Object],
      default: () => [],
    },
    tab: {
      type: [String, Number],
      default: null,
    },
    activatable: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    search: '',
    headers: [],
  }),
  computed: {
    defaultHeaders() {
      let headers = [
        {
          text: 'Loja',
          value: 'unit_name',
          width: '25%',
          isActive: true,
          isDefault: true,
          isRequired: false,
        },
        {
          text: 'Com o produto (Clube e não clube)',
          value: 'coupons_with_products',
          width: '15%',
          isActive: true,
          isDefault: true,
          isRequired: false,
        },
        {
          text: 'Do clube com produtos',
          value: 'coupons_club_with_products',
          width: '15%',
          isActive: true,
          isDefault: true,
          isRequired: false,
        },
        {
          text: 'Do clube com produtos (%)',
          value: 'coupons_club_with_products_percentage',
          width: '15%',
          isActive: true,
          isDefault: true,
          isRequired: false,
        },
        {
          text: 'Com quantidade elegivel',
          value: 'coupons_eligible',
          width: '15%',
          isActive: true,
          isDefault: true,
          isRequired: false,
        },
        {
          text: 'Com quantidade elegivel (%)',
          value: 'coupons_eligible_percentage',
          width: '15%',
          isActive: true,
          isDefault: true,
          isRequired: false,
        },
      ]
      if (this.activatable) {
        const activatableHeaders = [
          {
            text: 'Que ativaram a oferta',
            value: 'coupons_activated',
            isDefault: false,
            isRequired: false,
            isActive: false,
          },
          {
            text: 'Que ativaram a oferta (%)',
            value: 'coupons_activated_percentage',
            isDefault: false,
            isRequired: false,
            isActive: false,
          },
        ]
        headers.push(...activatableHeaders)
      }
      return headers
    },
    computedHeaders() {
      return this.headers.filter((header) => header.isActive)
    },
    headersTooltips() {
      return {
        unit_name: '',
        coupons_with_products: 'Total de cupons do período da oferta que tem ao menos um dos SKU participantes da oferta.',
        coupons_eligible: 'Cupons do clube que cumpriram as regras da oferta, respeitando quantidade mínima e máxima.',
        coupons_eligible_percentage: '',
        coupons_club_with_products: 'Cupons do clube do período da oferta que tem ao menos um dos SKU participantes da oferta.',
        coupons_club_with_products_percentage: '',
        coupons_activated: 'Cupons do clube que cumpriram as regras e o consumidor ativou a oferta no app, portanto obteve o desconto.',
        coupons_activated_percentage: '',
      }
    },
    footerProps() {
      return { itemsPerPageOptions: [10, 15, 20] }
    },
    couponsActivatedHighestValue() {
      return this.calcHighestValue('coupons_activated_percentage')
    },
    couponsClubHighestValue() {
      return this.calcHighestValue('coupons_club_with_products_percentage')
    },
    couponsEligibleHighestValue() {
      return this.calcHighestValue('coupons_eligible_percentage')
    },
    items() {
      const items = []
      const calculatedItems = []
      try {
        for (const unit of this.metrics) {
          const unitMetricsOfferType = unit.metrics?.[this.offerType]
          let item = {}
          if (this.offerType === 'combo') {
            if (this.tab) {
              item = {
                unit_name: unit.unit_name,
                coupons_with_products: unitMetricsOfferType?.products_metrics?.totals?.total?.tickets || 0,
                coupons_club_with_products: unitMetricsOfferType?.products_metrics?.totals?.total?.loyalty_tickets || 0,
              }
            } else {
              item = {
                unit_name: unit.unit_name,
                coupons_with_products: unitMetricsOfferType?.products_trigger_metrics?.totals?.total?.tickets || 0,
                coupons_club_with_products: unitMetricsOfferType?.products_trigger_metrics?.totals?.total?.loyalty_tickets || 0,
              }
            }
            item.coupons_eligible = unitMetricsOfferType?.eligible_metrics?.loyalty_tickets || 0
            item.coupons_activated = unitMetricsOfferType?.clients?.with_activated_sales?.tickets || 0
          } else {
            item = {
              unit_name: unit.unit_name,
              coupons_with_products: unitMetricsOfferType?.products_metrics?.totals?.total.tickets || 0,
              coupons_club_with_products: unitMetricsOfferType?.products_metrics?.totals?.total?.loyalty_tickets || 0,
              coupons_eligible: unitMetricsOfferType?.eligible_metrics?.loyalty_tickets || 0,
              coupons_activated: unitMetricsOfferType?.clients_metrics?.with_sales?.tickets || 0,
            }
          }
          items.push(item)
        }

        for (let item of items) {
          let calculatedItem = {
            ...item,
            coupons_club_with_products_percentage: this.calcPercent(item.coupons_with_products, item.coupons_club_with_products),
            coupons_eligible_percentage: this.calcPercent(item.coupons_club_with_products, item.coupons_eligible),
          }
          if (this.activatable) {
            calculatedItem.coupons_activated_percentage = this.calcPercent(item.coupons_eligible, item.coupons_activated)
          }
          calculatedItems.push(calculatedItem)
        }
      } catch (error) {
        console.warn(error)
      }
      return calculatedItems
    },
  },
  methods: {
    calcPercent(value1, value2) {
      return value1 > 0 ? (value2 * 100) / value1 : 0
    },

    calcHighestValue(property) {
      const coupons = []
      for (const item of this.items) {
        coupons.push(item[property])
      }

      coupons.sort((a, b) => b - a)
      return coupons[0]
    },

    getDataToExport() {
      const headers = this.computedHeaders.map((header) => header.text)
      const rows = this.items.map((item) => this.computedHeaders.map((header) => item[header.value]))
      return { headers, rows }
    },

    getDefaultHeadersOptions() {
      return cloneDeep(this.defaultHeaders)
    },
  },
}
</script>

<style lang="scss" scoped>
.funnel-table-container {
  ::v-deep .card-shadow {
    box-shadow: none !important;
  }
  .units-table {
    .units-table-unit-name {
      color: $cinza-d10;
      font-size: 14px;
      font-weight: 700;
      line-height: 21px;
      letter-spacing: 0em;
    }
  }
}
</style>
